import React, { useState, useEffect, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Build as RepairIcon,
  CheckCircle as ResolveIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import {
  fetchOpenDefects,
  fetchOpenDefectsCounts,
  addRepair,
  searchAssets,
} from '../redux/slice';
import { Loading, Checkbox, Tooltip, Pill } from '@gpstrackit/gtc-react-controls';
import FilterPanel from './filterPanel';
import SeverityBadge from './severityBadge';
import RepairModal from './repairModal';
import PhotoGalleryModal from '../../inspectionDetails/components/photoGalleryModal';
import { getInspectionLayouts } from '../../../../../services-px/inspectionApi';

const buildLayoutFilterData = (layouts) => {
  const zoneOptions = [];
  const componentOptions = [];
  const conditionOptions = [];

  const sortByName = (items) => (
    items.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
  );

  layouts.forEach((layout) => {
    const configZones = layout.data.configZones || [];
    const components = layout.data.components || [];

    configZones.forEach(({ tag }) => {
      if (tag && !zoneOptions.find((zone) => zone.id === tag)) {
        zoneOptions.push({
          id: tag,
          name: tag,
        });
      }
    });

    components.forEach(({ component, conditions }) => {
      if (component && !componentOptions.find((comp) => comp.id === component)) {
        componentOptions.push({
          id: component,
          name: component,
        });
      }

      (conditions || []).forEach((condition) => {
        if (condition && !conditionOptions.find((c) => c.id === condition)) {
          conditionOptions.push({
            id: condition,
            name: condition,
          });
        }
      });
    });
  });

  return {
    zoneOptions: sortByName(zoneOptions),
    componentOptions: sortByName(componentOptions),
    conditionOptions: sortByName(conditionOptions),
  };
};

const DefectsTable = ({ assetId = null, assetFleet = null, onBack = null, showBackButton = false }) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  // Access state directly in the UI
  const defects = useSelector((state) => state.defectsGtcRedux?.openDefects);
  const totalRecords = useSelector((state) => state.defectsGtcRedux?.openDefectsTotal || 0);
  const criticalCountFromServer = useSelector((state) => state.defectsGtcRedux?.openDefectsCriticalCount || 0);
  const warningCountFromServer = useSelector((state) => state.defectsGtcRedux?.openDefectsWarningCount || 0);
  const loading = useSelector((state) => state.defectsGtcRedux?.openDefectsLoading || false);
  const assetSearchResults = useSelector((state) => state.defectsGtcRedux?.assetSearchResults || []);
  const assetSearchLoading = useSelector((state) => state.defectsGtcRedux?.assetSearchLoading || false);
  const user = useSelector((state) => state.securityRedux?.user);

  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('latestInspDate');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [selectedDefects, setSelectedDefects] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  // Repair status filter - only used when not filtering by assetId (Repairs tab)
  const [repairedStatus, setRepairedStatus] = useState('open');
  const [expandedRows, setExpandedRows] = useState({});
  const [zoneOptions, setZoneOptions] = useState([]);
  const [componentOptions, setComponentOptions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  const loadDefects = useCallback(() => {
    // Build base params for counts (without pagination and sorting)
    const countParams = { ...filters };
    if (assetId) {
      countParams.assetId = assetId;
      // When viewing a specific asset's defects, always show only open defects
      countParams.repairedStatus = 'open';
    } else {
      // On Repairs tab (no assetId), use the repair status filter
      countParams.repairedStatus = repairedStatus;
      // If assets filter is set, extract assetIds
      if (filters.assets && filters.assets.length > 0) {
        countParams.assetIds = filters.assets.map((a) => a.id);
      }
    }
    // Extract names from multi-select filters
    if (filters.zones && filters.zones.length > 0) {
      countParams.zoneNames = filters.zones.map((z) => z.name);
    }
    if (filters.components && filters.components.length > 0) {
      countParams.componentNames = filters.components.map((c) => c.name);
    }
    if (filters.conditions && filters.conditions.length > 0) {
      countParams.conditionNames = filters.conditions.map((c) => c.name);
    }

    // Build search params by adding pagination and sorting to base params
    const searchParams = {
      ...countParams,
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage,
      sortField: orderBy,
      sortDirection: order,
    };

    dispatch(fetchOpenDefects(searchParams));
    dispatch(fetchOpenDefectsCounts(countParams));
  }, [dispatch, assetId, filters, repairedStatus, page, rowsPerPage, orderBy, order]);

  const fetchInspectionLayouts = useCallback(async () => {
    try {
      const layouts = await getInspectionLayouts();
      const { zoneOptions, componentOptions, conditionOptions } = buildLayoutFilterData(layouts || []);
      setZoneOptions(zoneOptions);
      setComponentOptions(componentOptions);
      setConditionOptions(conditionOptions);
    } catch (error) {
      console.error('Error loading inspection layouts', error);
    }
  }, []);

  useEffect(() => {
    fetchInspectionLayouts();
  }, [fetchInspectionLayouts]);

  useEffect(() => {
    setFilterOptions((prev) => ({
      ...prev,
      zones: zoneOptions,
      components: componentOptions,
      conditions: conditionOptions,
    }));
  }, [zoneOptions, componentOptions, conditionOptions]);

  // Merge components from actual defects with layout components
  useEffect(() => {
    if (!defects || defects.length === 0) return;

    // Extract unique component names from actual defects
    const defectComponentNames = defects
      .map((defect) => defect.componentName)
      .filter((component) => component && component.trim().length > 0);

    const uniqueDefectComponents = [...new Set(defectComponentNames)];

    // Get existing component names for comparison
    const existingComponentNames = componentOptions.map((c) => c.name);

    // Find components from defects that aren't in layouts
    const newComponents = uniqueDefectComponents
      .filter((component) => !existingComponentNames.includes(component))
      .map((component) => ({
        id: component,
        name: component,
      }));

    // If there are new components, merge and sort
    if (newComponents.length > 0) {
      const mergedComponents = [...componentOptions, ...newComponents];
      mergedComponents.sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      );
      setComponentOptions(mergedComponents);
    }
  }, [defects, componentOptions]);

  // Merge conditions from actual defects with layout conditions
  useEffect(() => {
    if (!defects || defects.length === 0) return;

    // Extract unique condition names from actual defects
    const defectConditionNames = defects
      .map((defect) => defect.conditionName)
      .filter((condition) => condition && condition.trim().length > 0);

    const uniqueDefectConditions = [...new Set(defectConditionNames)];

    // Get existing condition names for comparison
    const existingConditionNames = conditionOptions.map((c) => c.name);

    // Find conditions from defects that aren't in layouts
    const newConditions = uniqueDefectConditions
      .filter((condition) => !existingConditionNames.includes(condition))
      .map((condition) => ({
        id: condition,
        name: condition,
      }));

    // If there are new conditions, merge and sort
    if (newConditions.length > 0) {
      const mergedConditions = [...conditionOptions, ...newConditions];
      mergedConditions.sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      );
      setConditionOptions(mergedConditions);
    }
  }, [defects, conditionOptions]);

  const handleZoneSearch = useCallback((query) => {
    if (!query || query.trim().length === 0) return Promise.resolve([]);

    const normalizedQuery = query.trim().toLowerCase();
    const results = zoneOptions
      .filter((zone) => (zone.name || '').toLowerCase().includes(normalizedQuery))
      .slice(0, 20)
      .map((zone) => ({ ...zone }));
    return Promise.resolve(results);
  }, [zoneOptions]);

  const handleComponentSearch = useCallback((query) => {
    if (!query || query.trim().length === 0) return Promise.resolve([]);

    const normalizedQuery = query.trim().toLowerCase();
    const results = componentOptions
      .filter((component) => (component.name || '').toLowerCase().includes(normalizedQuery))
      .slice(0, 20)
      .map((component) => ({ ...component }));
    return Promise.resolve(results);
  }, [componentOptions]);

  const handleConditionSearch = useCallback((query) => {
    if (!query || query.trim().length === 0) return Promise.resolve([]);

    const normalizedQuery = query.trim().toLowerCase();
    const results = conditionOptions
      .filter((condition) => (condition.name || '').toLowerCase().includes(normalizedQuery))
      .slice(0, 20)
      .map((condition) => ({ ...condition }));
    return Promise.resolve(results);
  }, [conditionOptions]);

  // Search callback for FilterPanel - dispatches action, results come via Redux state
  const handleSearchAssets = useCallback((query) => {
    if (!query || query.trim().length === 0) return;
    dispatch(searchAssets({ query }));
  }, [dispatch]);

  const handlePhotoClick = useCallback((photos, index) => {
    if (!Array.isArray(photos) || photos.length === 0) {
      return;
    }

    const normalizedPhotos = photos.map((photo) => ({
      ...photo,
      fileKey: photo.fileKey || photo.file_key || photo.mediaId || photo.media_id,
      url: photo.url || photo.thumbnail || photo.path,
      thumbnail: photo.thumbnail || photo.url || photo.path,
    }));

    setGalleryPhotos(normalizedPhotos);
    setSelectedPhotoIndex(typeof index === 'number' ? index : 0);
    setGalleryOpen(true);
  }, []);

  const handleCloseGallery = useCallback(() => {
    setGalleryOpen(false);
  }, []);

  useEffect(() => {
    loadDefects();
  }, [loadDefects]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const getActiveFilters = () => {
    const active = [];
    // Handle multiple assets - create a chip for each selected asset
    if (filters.assets && filters.assets.length > 0) {
      filters.assets.forEach((asset) => {
        active.push({
          field: 'assetIds',
          filterKey: `assetIds-${asset.id}`,
          label: 'Asset',
          value: asset.fleet || asset.id,
          removeValue: asset.id,
        });
      });
    }
    // Handle multiple zones - create a chip for each selected zone
    if (filters.zones && filters.zones.length > 0) {
      filters.zones.forEach((zone) => {
        active.push({
          field: 'zoneIds',
          filterKey: `zoneIds-${zone.id}`,
          label: 'Zone',
          value: zone.name || zone.id,
          removeValue: zone.id,
        });
      });
    }
    // Handle multiple components - create a chip for each selected component
    if (filters.components && filters.components.length > 0) {
      filters.components.forEach((component) => {
        active.push({
          field: 'componentIds',
          filterKey: `componentIds-${component.id}`,
          label: 'Component',
          value: component.name || component.id,
          removeValue: component.id,
        });
      });
    }
    // Handle multiple conditions - create a chip for each selected condition
    if (filters.conditions && filters.conditions.length > 0) {
      filters.conditions.forEach((condition) => {
        active.push({
          field: 'conditionIds',
          filterKey: `conditionIds-${condition.id}`,
          label: 'Condition',
          value: condition.name || condition.id,
          removeValue: condition.id,
        });
      });
    }
    return active;
  };

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const handleSelectAll = (checked) => {
    // gtc-react-controls Checkbox passes boolean directly to onChange, not an event object
    if (checked) {
      // Only select defects that are repairable (not already repaired or closed)
      setSelected((defects || []).filter((d) => !d.repaired && !d.ignored).map((d) => d.defectId));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (defectId, defect) => {
    // Prevent selecting repaired or closed defects
    if (defect && (defect.repaired || defect.ignored)) {
      return;
    }
    setSelected((prev) =>
      prev.includes(defectId)
        ? prev.filter((id) => id !== defectId)
        : [...prev, defectId]
    );
  };

  const toggleRowExpanded = (defectId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [defectId]: !prev[defectId],
    }));
  };

  const parseRepairData = (dataString) => {
    if (!dataString) return {};
    try {
      return JSON.parse(dataString);
    } catch {
      return {};
    }
  };

  const handleOpenRepairModal = (defectsToRepair) => {
    // Accept single defect or array
    const defectsArray = Array.isArray(defectsToRepair) ? defectsToRepair : [defectsToRepair];
    setSelectedDefects(defectsArray);
    setRepairModalOpen(true);
  };

  const handleRepairSubmit = async (repairData) => {
    try {
      await dispatch(addRepair(repairData)).unwrap();
      // Clear selection immediately for better UX
      setSelected([]);
      // Refresh data to ensure consistency with server
      await loadDefects();
    } catch (error) {
      console.error('Error submitting repair:', error);
      // Still try to refresh to get current state
      loadDefects();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatRepairDate = (value) => {
    if (!value) return 'N/A';

    const parsedUtc = moment.utc(value, 'YYYY-MM-DD HH:mm:ss.SSSSSS', true);
    if (parsedUtc.isValid()) {
      return parsedUtc.local().format('M/D/YYYY, h:mm:ss A');
    }

    const fallback = moment.utc(value);
    if (fallback.isValid()) {
      return fallback.local().format('M/D/YYYY, h:mm:ss A');
    }

    return 'N/A';
  };

  // Use server counts (from all filtered results, not just current page)
  const criticalCount = criticalCountFromServer;
  const warningCount = warningCountFromServer;

  // Hide the actions and checkbox columns when viewing repaired or closed defects
  const showActionsColumn = repairedStatus !== 'repaired' && repairedStatus !== 'ignored';
  const showSelectColumn = repairedStatus !== 'repaired' && repairedStatus !== 'ignored';

  // Helper to check if a defect can have repairs added (not already repaired or closed)
  const isDefectRepairable = (defect) => !defect.repaired && !defect.ignored;

  // Get only the defects that can be selected (open defects)
  const selectableDefects = (defects || []).filter(isDefectRepairable);

  const columns = [
    ...(showSelectColumn ? [{ id: 'select', label: '', sortable: false, width: 50 }] : []),
    { id: 'assetLabel', label: messages.asset || 'Asset', sortable: true },
    { id: 'zoneName', label: messages.zone || 'Zone', sortable: false },
    { id: 'componentName', label: messages.component || 'Component', sortable: false },
    { id: 'conditionName', label: messages.condition || 'Condition', sortable: false },
    { id: 'ok', label: messages.severity || 'Severity', sortable: true },
    { id: 'latestInspDate', label: messages.inspectionDate || 'Inspection Date', sortable: true },
    { id: 'repaired', label: messages.status || 'Status', sortable: true },
    ...(showActionsColumn ? [{ id: 'actions', label: messages.actions || 'Actions', sortable: false }] : []),
    { id: 'expand', label: '', sortable: false, width: 40 },
  ];

  // Show full-page loading on initial load
  if (loading && defects === null) {
    return <Loading full />;
  }

  // Get context-aware empty state messages based on current filter
  const getEmptyStateMessages = () => {
    // Check if any filters are active (excluding repairedStatus)
    const hasActiveFilters = Object.keys(filters).some(
      (key) => filters[key] && (Array.isArray(filters[key]) ? filters[key].length > 0 : true)
    );

    if (repairedStatus === 'repaired') {
      return {
        title: messages.noRepairedDefects || 'No repaired defects',
        subtitle: hasActiveFilters
          ? (messages.noDefectsMatchFilters || 'No defects match the current filters')
          : (messages.noDefectsRepaired || 'No defects have been repaired'),
      };
    }
    if (repairedStatus === 'ignored') {
      return {
        title: messages.noClosedDefects || 'No closed defects',
        subtitle: hasActiveFilters
          ? (messages.noDefectsMatchFilters || 'No defects match the current filters')
          : (messages.noDefectsClosed || 'No defects have been closed'),
      };
    }
    if (repairedStatus === 'all') {
      return {
        title: messages.noDefectsFound || 'No defects found',
        subtitle: hasActiveFilters
          ? (messages.noDefectsMatchFilters || 'No defects match the current filters')
          : (messages.noDefectsRecorded || 'No defects have been recorded'),
      };
    }
    // Default: open defects
    return {
      title: messages.noOpenDefects || 'No open defects',
      subtitle: messages.allDefectsResolved || 'All defects have been resolved',
    };
  };

  // Determine title based on whether we're filtering by asset and repair status
  const getTitle = () => {
    if (assetId) {
      return assetFleet
        ? (messages.defectsFor || "Defects for {assetFleet}").replace('{assetFleet}', assetFleet)
        : (messages.openDefectDetails || 'Open Defect Details');
    }
    // On Repairs tab, show title based on filter
    if (repairedStatus === 'open') {
      return messages.openDefects || 'Open Defects';
    }
    if (repairedStatus === 'repaired') {
      return messages.repairedDefects || 'Repaired Defects';
    }
    if (repairedStatus === 'ignored') {
      return messages.closedDefects || 'Closed Defects';
    }
    return messages.allDefects || 'All Defects';
  };

  return (
    <Box>
      <Box className="page-header">
        <Box className="title-section">
          {showBackButton && onBack && (
            <Button
              startIcon={<BackIcon />}
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              {messages.back || "Back"}
            </Button>
          )}
          <Typography className="page-title">{getTitle()}</Typography>
          <span className="record-count">{totalRecords} {messages.defects || "defects"}</span>
        </Box>
        <Box className="action-buttons">
          {selected.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ResolveIcon />}
              onClick={() => {
                // Get all selected defects
                const defectsToRepair = (defects || []).filter((d) => selected.includes(d.defectId));
                handleOpenRepairModal(defectsToRepair);
              }}
            >
              {(messages.repairSelected || "Repair Selected ({count})").replace('{count}', selected.length)}
            </Button>
          )}
        </Box>
      </Box>

      <Box className="summary-cards">
        <Box className="summary-card critical">
          <span className="card-label">{messages.criticalDefects || "Critical Defects"}</span>
          <span className="card-value">{criticalCount}</span>
        </Box>
        <Box className="summary-card warning">
          <span className="card-label">{messages.nonCriticalDefects || "Non-Critical Defects"}</span>
          <span className="card-value">{warningCount}</span>
        </Box>
        <Box className="summary-card info">
          <span className="card-label">{messages.totalDefects || "Total Defects"}</span>
          <span className="card-value">{totalRecords}</span>
        </Box>
      </Box>

      <FilterPanel
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        activeFilters={getActiveFilters()}
        onSearchAssets={handleSearchAssets}
        assetSearchResults={assetSearchResults}
        assetSearchLoading={assetSearchLoading}
        onSearchZones={handleZoneSearch}
        onSearchComponents={handleComponentSearch}
        onSearchConditions={handleConditionSearch}
        repairedStatusFilter={!assetId ? (
          <FormControl className="filter-field" size="small">
            <InputLabel>{messages.defectStatus || 'Defect Status'}</InputLabel>
            <MuiSelect
              value={repairedStatus}
              label={messages.defectStatus || 'Defect Status'}
              onChange={(e) => {
                setRepairedStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">{messages.allDefects || 'Show All'}</MenuItem>
              <MenuItem value="open">{messages.openDefects || 'Open'}</MenuItem>
              <MenuItem value="ignored">{messages.closedDefects || 'Closed'}</MenuItem>
              <MenuItem value="repaired">{messages.repairedDefects || 'Repaired'}</MenuItem>
            </MuiSelect>
          </FormControl>
        ) : null}
      />

      <Paper className="data-table">
        <TableContainer className="defects-table-container">
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {showSelectColumn && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      isIndeterminate={selected.length > 0 && selected.length < selectableDefects.length}
                      checked={selectableDefects.length > 0 && selected.length === selectableDefects.length}
                      onChange={handleSelectAll}
                      isDisabled={selectableDefects.length === 0}
                    />
                  </TableCell>
                )}
                {columns.filter(col => col.id !== 'select').map((column) => (
                  <TableCell key={column.id} style={column.width ? { width: column.width } : {}}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Loading size={32} />
                  </TableCell>
                </TableRow>
              ) : (defects || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Box className="no-results">
                      <Typography variant="h6">{getEmptyStateMessages().title}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {getEmptyStateMessages().subtitle}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                (defects || []).map((defect) => {
                  const hasRepairs = defect.repairs && defect.repairs.length > 0;
                  const hasPhotos = defect.photos && defect.photos.length > 0;
                  const hasExpandableContent = hasRepairs || hasPhotos;
                  const isExpanded = !!expandedRows[defect.defectId];

                  return (
                    <Fragment key={defect.defectId}>
                      <TableRow
                        hover
                        selected={selected.includes(defect.defectId)}
                        className={!defect.ok ? 'critical' : 'warning'}
                      >
                        {showSelectColumn && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.includes(defect.defectId)}
                              onChange={() => handleSelect(defect.defectId, defect)}
                              isDisabled={!isDefectRepairable(defect)}
                            />
                          </TableCell>
                        )}
                        <TableCell>{defect.asset?.label || defect.assetFleet || 'N/A'}</TableCell>
                        <TableCell>{defect.zoneName}</TableCell>
                        <TableCell>{defect.componentName}</TableCell>
                        <TableCell>{defect.conditionName}</TableCell>
                        <TableCell>
                          <SeverityBadge isCritical={defect.severity === 255} />
                        </TableCell>
                        <TableCell>{formatDate(defect.latestInspDate)}</TableCell>
                        <TableCell>
                          <Pill
                            item={{
                              label: defect.repaired ? (messages.repaired || 'Repaired') : defect.ignored ? (messages.closedStatus || 'Closed') : (messages.open || 'Open'),
                              color: (defect.repaired || defect.ignored) ? '#2e7d32' : '#ed6c02',
                            }}
                            variant="outlined"
                            clickable={false}
                            style={{ cursor: 'unset !important' }}
                          />
                        </TableCell>
                        {showActionsColumn && (
                          <TableCell>
                            <Tooltip title={isDefectRepairable(defect) ? (messages.addRepair || "Add Repair") : (messages.alreadyRepaired || "Already repaired or closed")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenRepairModal(defect)}
                                  disabled={!isDefectRepairable(defect)}
                                >
                                  <RepairIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell padding="none" sx={{ width: 40 }}>
                          {hasExpandableContent && (
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpanded(defect.defectId)}
                              aria-label={isExpanded ? (messages.collapse || 'Collapse') : (messages.expand || 'Expand')}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      {hasExpandableContent && (
                        <TableRow className="expand-row">
                          <TableCell
                            colSpan={columns.length}
                            sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box className="repair-details">
                                {hasRepairs && (
                                  <>
                                    <Typography variant="subtitle2" className="repair-details-title">
                                      {messages.repairs || 'Repairs'}
                                    </Typography>
                                    {defect.repairs.map((repair, idx) => {
                                      const repairData = parseRepairData(repair.data);
                                      return (
                                        <Box key={idx} className="repair-entry">
                                          <Box className="repair-field">
                                            <span className="repair-label">{messages.repairDate || 'Repair Date'}</span>
                                            <span className="repair-value">{formatRepairDate(repair.createdOn)}</span>
                                          </Box>
                                          {(repairData.mechanicFirstName || repairData.mechanicLastName) && (
                                            <Box className="repair-field">
                                              <span className="repair-label">{messages.mechanic || 'Mechanic'}</span>
                                              <span className="repair-value">
                                                {[repairData.mechanicFirstName, repairData.mechanicLastName].filter(Boolean).join(' ')}
                                              </span>
                                            </Box>
                                          )}
                                          {repairData.comment && (
                                            <Box className="repair-field">
                                              <span className="repair-label">{messages.comment || 'Comment'}</span>
                                              <span className="repair-value wrap-text">{repairData.comment}</span>
                                            </Box>
                                          )}
                                          {repairData.resolution && (
                                            <Box className="repair-field">
                                              <span className="repair-label">{messages.resolution || 'Resolution'}</span>
                                              <span className="repair-value">{repairData.resolution}</span>
                                            </Box>
                                          )}
                                          {repairData.workOrder && (
                                            <Box className="repair-field">
                                              <span className="repair-label">{messages.workOrder || 'Work Order'}</span>
                                              <span className="repair-value">{repairData.workOrder}</span>
                                            </Box>
                                          )}
                                          {repairData.status && (
                                            <Box className="repair-field">
                                              <span className="repair-label">{messages.status || 'Status'}</span>
                                              <span className="repair-value">{repairData.status}</span>
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </>
                                )}
                                {hasPhotos && (
                                  <Box className="repair-photos">
                                    <Typography variant="subtitle2" className="repair-details-title">
                                      {messages.defectPhotos || 'Defect Photos'}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                      {defect.photos.map((photo, idx) => (
                                        <img
                                          key={photo.mediaId || idx}
                                          src={photo.url}
                                          alt={`Defect photo ${idx + 1}`}
                                          style={{
                                            width: 80,
                                            height: 80,
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            border: '1px solid #ddd',
                                          }}
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => handlePhotoClick(defect.photos, idx)}
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                              event.preventDefault();
                                              handlePhotoClick(defect.photos, idx);
                                            }
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      <RepairModal
        open={repairModalOpen}
        onClose={() => {
          setRepairModalOpen(false);
          setSelectedDefects([]);
        }}
        onSubmit={handleRepairSubmit}
        defects={selectedDefects}
        userInfo={user}
      />
      <PhotoGalleryModal
        open={galleryOpen}
        onClose={handleCloseGallery}
        photos={galleryPhotos}
        initialIndex={selectedPhotoIndex}
      />
    </Box>
  );
};

DefectsTable.propTypes = {
  assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assetFleet: PropTypes.string,
  onBack: PropTypes.func,
  showBackButton: PropTypes.bool,
};

export default DefectsTable;
